import { Client, Coach } from "@/types/page";
import EventEmitter from "events";

export type ConsoleEvent = {
    type: "console";
    data: {
        level: "log" | "info" | "warn" | "error";
        message: unknown[];
    };
};

export type OpenExternalLinkEvent = {
    type: "openExternalLink";
    data: string;
};

export type UserChangedEvent = {
    type: "userChanged";
    data: Client | Coach | null;
};

export type RequestTrackingEvent = {
    type: "requestTracking";
    data: Record<string, never>;
};

export type AppInitializedEvent = {
    type: "appInitialized";
    data: Record<string, never>;
};

export type IsAliveEvent = {
    type: "isAlive";
    data: boolean;
};

export type SetNotificationBadgeEvent = {
    type: "setNotificationBadge";
    data: number;
};

export type RequestAppStoreReviewEvent = {
    type: "requestAppStoreReview";
    data: Record<string, never>;
};

export type OpenGalleryEvent = {
    type: "openGallery";
    data: string[];
};

export type NavbarBgColorChangedEvent = {
    type: "navbarBgColorChanged";
    data: {
        navbarBgColor: string;
        bottomBgColor: string;
    };
};

export type WebToAppEvent =
    | ConsoleEvent
    | OpenExternalLinkEvent
    | UserChangedEvent
    | RequestTrackingEvent
    | AppInitializedEvent
    | IsAliveEvent
    | SetNotificationBadgeEvent
    | RequestAppStoreReviewEvent
    | OpenGalleryEvent
    | NavbarBgColorChangedEvent;

export type SetTrackingStatusEvent = {
    type: "setTrackingStatus";
    data: boolean;
};

export type WebViewAliveCheckEvent = {
    type: "webViewAliveCheck";
    data: Record<string, never>;
};

export type AppToWebEvent = SetTrackingStatusEvent | WebViewAliveCheckEvent;

export function sendEventToApp<T extends WebToAppEvent["type"]>(
    eventType: T,
    data: Extract<WebToAppEvent, { type: T }>["data"],
) {
    if (window.ReactNativeWebView) {
        const event = {
            type: eventType,
            data: data,
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(event));
    }
}

window.appEvents = new EventEmitter();

export function listenToAppEvent<T extends AppToWebEvent["type"]>(
    eventName: T,
    callback: (data: Extract<AppToWebEvent, { type: T }>["data"]) => void,
) {
    window.appEvents.on(eventName, callback);
    return () => window.appEvents.off(eventName, callback);
}

export function shareMobileConsole() {
    const consoleLevels = ["log", "info", "warn", "error"] as const;

    const preprocessArgs = (args: unknown[]) => {
        return args.map((arg) => {
            if (arg instanceof Error) {
                return arg.toString() + "\n" + arg.stack;
            }
            return arg;
        });
    };

    consoleLevels.forEach((level) => {
        const originalMethod = console[level];
        console[level] = function (...args: unknown[]) {
            originalMethod(...args);
            sendEventToApp("console", {
                level,
                message: preprocessArgs(args),
            });
        };
    });
}

export function addHeaderToRequests() {
    window.axios.interceptors.request.use((config) => {
        config.headers["X-Mobile-App"] = "true";
        return config;
    });
}

export function mobileAppSetup() {
    if (!window.isMobileApp || !window.ReactNativeWebView) return;
    shareMobileConsole();
    addHeaderToRequests();

    // Disable zoom on mobile app
    const meta = document.createElement("meta");
    meta.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0",
    );
    meta.setAttribute("name", "viewport");
    document.getElementsByTagName("head")[0].appendChild(meta);
}
